import { useQuery } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { TreeNode } from 'common/components/catalog/Catalog'
import { useAppSelector } from 'common/hooks/redux'

import {
  CustomerAttributes,
  fetchVariants,
  PersonalizationRegion,
} from '../api'
import { personalizationKeys } from '../queryKeys'

const transformVariant = (variant: any) => {
  const { channel, variantText, sends } = variant
  const additionalFields =
    'opens' in variant
      ? { clicks: variant.clicks, opens: variant.opens }
      : { totalOpens: variant.totalOpens, directOpens: variant.directOpens }
  return { channel, variantText, sends, ...additionalFields }
}

const useGetVariantsQuery = ({
  personalizationId,
  customerAttributes,
  productCategories,
  personalizationRegion,
}: {
  personalizationId: string
  customerAttributes: CustomerAttributes | undefined
  productCategories: TreeNode | undefined
  personalizationRegion: PersonalizationRegion | undefined
}) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const flags = useFlags()

  const query = useQuery(
    personalizationKeys.variants({
      accountId,
      personalizationId,
      customerAttributes,
      productCategories,
    }),
    () =>
      fetchVariants({
        accountId,
        personalizationId,
        customerAttributes,
        productCategories,
        ...(flags.switchToRegionalizedData && {
          personalizationRegion: personalizationRegion?.toLocaleLowerCase(),
        }),
      }),
    {
      select: ({ data: { totalCount, filteredCount, items } }) => {
        return {
          totalCount: totalCount,
          filteredCount: filteredCount,
          variants: items.map(transformVariant),
        }
      },
    }
  )

  return query
}

export default useGetVariantsQuery
